"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import type { Workspace } from "@/lib/api";
import {
  fetchReceivableDebtsSummary,
  resolveOwnerUserId,
  summarizeReceivableDebts,
  type ReceivableDebtsSummary,
} from "@/lib/pendingOrders";

type RefetchOptions = {
  silent?: boolean;
};

const EMPTY_SUMMARY: ReceivableDebtsSummary = {
  pendingCount: 0,
  reviewCount: 0,
  totalCount: 0,
  cardSubtitle: "ไม่มีรายการค้าง",
  detailNowLabel: "ไม่มีรายการค้างชำระ",
};

export function useWorkspaceReceivableSummary(workspace?: Workspace | null) {
  const ownerUserId = useMemo(
    () => resolveOwnerUserId(workspace),
    [workspace],
  );
  const [summary, setSummary] = useState<ReceivableDebtsSummary>(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      if (!ownerUserId) {
        setSummary(EMPTY_SUMMARY);
        setError(null);
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;

      try {
        if (!silent) setIsLoading(true);
        setError(null);
        const next = await fetchReceivableDebtsSummary(ownerUserId);
        setSummary(next);
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
        setSummary((prev) => summarizeReceivableDebts(null, null));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [ownerUserId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useWorkspaceActivityAutoRefresh(workspace?.id, () => refetch({ silent: true }));

  const cardSubtitle = isLoading
    ? "กำลังโหลด…"
    : error
      ? "โหลดไม่สำเร็จ"
      : summary.cardSubtitle;

  const detailNowLabel = isLoading
    ? "กำลังโหลด…"
    : error
      ? "โหลดไม่สำเร็จ"
      : summary.detailNowLabel;

  return {
    ownerUserId,
    summary,
    cardSubtitle,
    detailNowLabel,
    isLoading,
    error,
    refetch,
  };
}
